import {
  DEFAULT_OAUTH_CALLBACK_URL,
  resolveOAuthCallbackUrl,
} from '@/lib/auth/oauthCallbackUrl'
import { isNativeCapacitorApp } from '@/lib/pwa/isNativeCapacitorApp'
import { isStandalone } from '@/lib/pwa/isStandalone'
import {
  CAPACITOR_HANDOFF_PARAM,
  clearCapacitorHandoffInProgress,
  isCapacitorHandoffInProgress,
  logCapacitorOAuth,
} from '@/lib/auth/capacitorSessionHandoff'

/** Custom URL scheme registered in iOS Info.plist and capacitor.config.ts */
export const WAGAMOUR_APP_SCHEME = 'wagamour'

/** Query flag threaded through NextAuth callbackUrl from Capacitor OAuth. */
export const CAPACITOR_NATIVE_OAUTH_PARAM = 'native'
export const CAPACITOR_NATIVE_OAUTH_VALUE = 'capacitor'
export const CAPACITOR_NATIVE_NEXT_PARAM = 'next'
/** Correlates SFVC OAuth browser with WKWebView for poll-based handoff when deep link is swallowed. */
export const CAPACITOR_HANDOFF_SESSION_PARAM = 'cap_hsid'
export const CAPACITOR_HANDOFF_SESSION_STORAGE_KEY = 'wagamour_cap_hsid'

/** Stashed in Capacitor WebView before OAuth; read when app regains focus after deep link */
export const CAPACITOR_PENDING_OAUTH_RETURN_KEY =
  'wagamour_pending_oauth_return'

/** Set in ASWebAuthenticationSession when native OAuth handoff starts (survives redirect to /app-beta). */
export const CAPACITOR_OAUTH_BROWSER_SESSION_KEY =
  'wagamour_capacitor_oauth_browser'

/** In-app browser tab session: OAuth flow started or completed in this SFVC context. */
export const CAPACITOR_OAUTH_FLOW_SESSION_KEY = 'wagamour_capacitor_oauth_flow'

/** App routes where an authenticated in-app browser should hand off to the native shell. */
export const CAPACITOR_AUTHENTICATED_HANDOFF_PATHS = new Set([
  '/app-beta',
  '/auth/complete',
  '/play/connect/resume',
])

export function markCapacitorOAuthBrowserSession(): void {
  if (typeof window === 'undefined') return
  try {
    sessionStorage.setItem(CAPACITOR_OAUTH_BROWSER_SESSION_KEY, '1')
  } catch {
    // ignore quota / private mode
  }
}

export function hasCapacitorOAuthBrowserSession(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return sessionStorage.getItem(CAPACITOR_OAUTH_BROWSER_SESSION_KEY) === '1'
  } catch {
    return false
  }
}

export function clearCapacitorOAuthBrowserSession(): void {
  if (typeof window === 'undefined') return
  try {
    sessionStorage.removeItem(CAPACITOR_OAUTH_BROWSER_SESSION_KEY)
  } catch {
    // ignore
  }
}

/**
 * True inside ASWebAuthenticationSession / Safari auth chrome after Capacitor OAuth.
 * Never true in the Capacitor WebView shell or normal web/PWA tabs.
 */
export function markCapacitorOAuthFlowInBrowser(): void {
  if (typeof window === 'undefined') return
  markCapacitorOAuthBrowserSession()
  try {
    sessionStorage.setItem(CAPACITOR_OAUTH_FLOW_SESSION_KEY, '1')
  } catch {
    // ignore
  }
}

export function hasCapacitorOAuthFlowInBrowser(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return sessionStorage.getItem(CAPACITOR_OAUTH_FLOW_SESSION_KEY) === '1'
  } catch {
    return false
  }
}

export function clearCapacitorOAuthFlowInBrowser(): void {
  if (typeof window === 'undefined') return
  try {
    sessionStorage.removeItem(CAPACITOR_OAUTH_FLOW_SESSION_KEY)
  } catch {
    // ignore
  }
}

/** True when SFVC landed here immediately after NextAuth OAuth callback. */
export function cameFromOAuthCallbackReferrer(): boolean {
  if (typeof document === 'undefined') return false
  return /\/api\/auth\/callback\/(google|apple)/.test(document.referrer)
}

/** True when SFVC landed on app shell after handoff consume (session cookie set in SFVC). */
export function cameFromCapacitorHandoffConsumeReferrer(): boolean {
  if (typeof document === 'undefined') return false
  return /\/api\/auth\/capacitor-handoff\/consume/.test(document.referrer)
}

/**
 * Hard gate for CapacitorOAuthBrowserReturn overlay + handoff token issue.
 * Never pathname, referrer, session status, or bare authenticated state.
 *
 * Allowed:
 * 1. ?native=capacitor
 * 2. ?cap_hsid= (URL only)
 * 3. wagamour_capacitor_oauth_browser session marker AND cap_hsid in URL or storage (iOS SFVC launch)
 */
export function shouldAllowCapacitorOAuthBrowserHandoffOverlay(
  params?: Pick<URLSearchParams, 'get'> | null
): boolean {
  if (typeof window === 'undefined') return false
  if (isNativeCapacitorApp()) return false
  // Installed PWA is never the Cap SFVC sheet — ignore OAuth storage leftovers.
  if (isStandalone()) return false

  const p =
    params ??
    (typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search)
      : null)
  if (!p) return false

  if (isCapacitorNativeReturn(p)) return true
  if (p.get(CAPACITOR_HANDOFF_SESSION_PARAM)?.trim()) return true

  if (
    typeof window !== 'undefined' &&
    window.location.pathname === '/app-beta' &&
    (cameFromOAuthCallbackReferrer() ||
      cameFromCapacitorHandoffConsumeReferrer())
  ) {
    return true
  }

  if (hasCapacitorOAuthBrowserSession() && peekCapacitorHandoffSessionId()) {
    return true
  }

  return false
}

/** @deprecated Use shouldAllowCapacitorOAuthBrowserHandoffOverlay */
export function hasExplicitNativeOAuthReturnMarkers(
  params?: Pick<URLSearchParams, 'get'> | null
): boolean {
  return shouldAllowCapacitorOAuthBrowserHandoffOverlay(params)
}

/**
 * True when the Capacitor in-app browser (not WKWebView) should dismiss after OAuth.
 */
export function shouldDismissCapacitorOAuthBrowser(
  params?: Pick<URLSearchParams, 'get'> | null
): boolean {
  if (typeof window !== 'undefined' && isNativeCapacitorApp()) {
    return false
  }
  return shouldAllowCapacitorOAuthBrowserHandoffOverlay(params)
}

/**
 * True when running in the Capacitor in-app OAuth browser (SFSafariViewController), not WKWebView
 * or a normal desktop/mobile browser tab.
 */
export function isCapacitorInAppBrowserContext(): boolean {
  if (typeof window === 'undefined') return false
  if (isNativeCapacitorApp()) return false
  return shouldAllowCapacitorOAuthBrowserHandoffOverlay()
}

/**
 * True when the global handoff overlay should run (OAuth return in progress in in-app browser).
 */
export function shouldShowCapacitorOAuthBrowserHandoff(
  params?: Pick<URLSearchParams, 'get'> | null
): boolean {
  return shouldAllowCapacitorOAuthBrowserHandoffOverlay(params)
}

/**
 * Authenticated user in the in-app browser on an app shell route — force handoff to WKWebView.
 * /auth/complete: explicit OAuth return markers in URL.
 * /app-beta: only when an in-browser-sheet OAuth flow flag was set on /auth/complete (not pathname-only).
 */
export function shouldForceAuthenticatedBrowserHandoff(
  sessionStatus: string,
  pathname?: string
): boolean {
  if (typeof window === 'undefined') return false
  if (isNativeCapacitorApp()) return false
  if (sessionStatus !== 'authenticated') return false

  const path = pathname ?? window.location.pathname

  if (
    path === '/app-beta' &&
    (hasCapacitorOAuthFlowInBrowser() ||
      cameFromOAuthCallbackReferrer() ||
      cameFromCapacitorHandoffConsumeReferrer())
  ) {
    return true
  }

  if (!CAPACITOR_AUTHENTICATED_HANDOFF_PATHS.has(path)) return false

  const params = new URLSearchParams(window.location.search)
  return shouldShowCapacitorOAuthBrowserHandoff(params)
}

export function createCapacitorHandoffSessionId(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID()
  }
  return `hsid_${Date.now()}_${Math.random().toString(36).slice(2)}`
}

export function stashCapacitorHandoffSessionId(hsid: string): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(CAPACITOR_HANDOFF_SESSION_STORAGE_KEY, hsid)
    sessionStorage.setItem(CAPACITOR_HANDOFF_SESSION_STORAGE_KEY, hsid)
  } catch {
    // ignore
  }
}

export function peekCapacitorHandoffSessionId(): string | null {
  if (typeof window === 'undefined') return null
  try {
    return (
      sessionStorage.getItem(CAPACITOR_HANDOFF_SESSION_STORAGE_KEY) ??
      localStorage.getItem(CAPACITOR_HANDOFF_SESSION_STORAGE_KEY)
    )
  } catch {
    return null
  }
}

export function readCapacitorHandoffSessionIdFromUrl(
  params?: URLSearchParams | null
): string | null {
  const fromParams =
    params?.get(CAPACITOR_HANDOFF_SESSION_PARAM) ??
    (typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get(
          CAPACITOR_HANDOFF_SESSION_PARAM
        )
      : null)
  if (fromParams?.trim()) {
    try {
      sessionStorage.setItem(
        CAPACITOR_HANDOFF_SESSION_STORAGE_KEY,
        fromParams.trim()
      )
      markCapacitorOAuthFlowInBrowser()
    } catch {
      // ignore
    }
    return fromParams.trim()
  }
  return peekCapacitorHandoffSessionId()
}

export function clearCapacitorHandoffSessionId(): void {
  if (typeof window === 'undefined') return
  try {
    sessionStorage.removeItem(CAPACITOR_HANDOFF_SESSION_STORAGE_KEY)
    localStorage.removeItem(CAPACITOR_HANDOFF_SESSION_STORAGE_KEY)
  } catch {
    // ignore
  }
}

/** Clear WKWebView poll/OAuth handoff flags so UI is not stuck after failed or stale poll. */
export function clearCapacitorHandoffPollClientState(reason: string): void {
  if (typeof window === 'undefined') return
  consumeCapacitorOAuthReturnPath()
  clearCapacitorHandoffSessionId()
  clearCapacitorOAuthBrowserSession()
  clearCapacitorOAuthFlowInBrowser()
  clearCapacitorHandoffInProgress()
  logCapacitorOAuth('handoff.poll.cleared', { reason })
}

export function buildCapacitorOAuthCallbackUrl(
  nextPath: string = DEFAULT_OAUTH_CALLBACK_URL,
  handoffSessionId?: string | null
): string {
  const next = resolveOAuthCallbackUrl(nextPath)
  const q = new URLSearchParams({
    [CAPACITOR_NATIVE_OAUTH_PARAM]: CAPACITOR_NATIVE_OAUTH_VALUE,
    [CAPACITOR_NATIVE_NEXT_PARAM]: next,
  })
  if (handoffSessionId?.trim()) {
    q.set(CAPACITOR_HANDOFF_SESSION_PARAM, handoffSessionId.trim())
  }
  return `/auth/complete?${q.toString()}`
}

/** wagamour://return?next=…&handoff=… for dismissing auth browser back into the app shell. */
export function buildCapacitorReturnDeepLink(
  nextPath?: string | null,
  handoffToken?: string | null
): string {
  const path = resolveOAuthCallbackUrl(nextPath ?? DEFAULT_OAUTH_CALLBACK_URL)
  const url = new URL(`${WAGAMOUR_APP_SCHEME}://return`)
  url.searchParams.set(CAPACITOR_NATIVE_NEXT_PARAM, path)
  if (handoffToken?.trim()) {
    url.searchParams.set(CAPACITOR_HANDOFF_PARAM, handoffToken.trim())
  }
  return url.toString()
}

/**
 * Exact NextAuth callbackUrl for Capacitor — never /app-beta (loads inside Safari auth UI).
 */
export const CAPACITOR_OAUTH_CALLBACK_URL = buildCapacitorOAuthCallbackUrl(
  DEFAULT_OAUTH_CALLBACK_URL
)

/** Manual native handoff link after OAuth (auth/complete "Back to app"). */
export const CAPACITOR_RETURN_DEEP_LINK = buildCapacitorReturnDeepLink(
  DEFAULT_OAUTH_CALLBACK_URL
)

export function isCapacitorNativeReturn(
  params: Pick<URLSearchParams, 'get'> | null | undefined
): boolean {
  return (
    params?.get(CAPACITOR_NATIVE_OAUTH_PARAM) === CAPACITOR_NATIVE_OAUTH_VALUE
  )
}

/**
 * True when Cap OAuth completion / SFVC handoff UX should run.
 *
 * Requires explicit Cap return markers — never bare `/auth/complete` pathname.
 * Ordinary web (preview or production) must stay on the NextAuth session → router.replace path
 * and must not call `/api/auth/capacitor-handoff/issue`.
 *
 * Allowed:
 * 1. `?native=capacitor`
 * 2. `?cap_hsid=`
 * 3. Cap in-app browser session marker + stored handoff session id (SFVC after bridge open)
 */
export function shouldAttemptCapacitorNativeReturn(
  params?: Pick<URLSearchParams, 'get'> | null
): boolean {
  const p =
    params ??
    (typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search)
      : null)
  if (!p) return false

  if (isCapacitorNativeReturn(p)) return true
  if (p.get(CAPACITOR_HANDOFF_SESSION_PARAM)?.trim()) return true

  if (typeof window !== 'undefined') {
    if (hasCapacitorOAuthBrowserSession() && peekCapacitorHandoffSessionId()) {
      return true
    }
  }

  return false
}

/** Suppress in-app auth modal while native OAuth / handoff is in flight (WebView only). */
export function shouldSuppressAuthPromptDuringNativeOAuth(): boolean {
  if (typeof window === 'undefined') return false
  if (!isNativeCapacitorApp()) return false
  if (isCapacitorHandoffInProgress()) return true
  return hasPendingCapacitorOAuthReturn()
}

export function resolveNativeReturnPath(
  params: Pick<URLSearchParams, 'get'> | null | undefined,
  fallback?: string | null
): string {
  return resolveOAuthCallbackUrl(
    params?.get(CAPACITOR_NATIVE_NEXT_PARAM) ??
      peekCapacitorOAuthReturnPath() ??
      fallback ??
      DEFAULT_OAUTH_CALLBACK_URL
  )
}

export function peekCapacitorOAuthReturnPath(): string | null {
  if (typeof window === 'undefined') return null
  try {
    const value =
      sessionStorage.getItem(CAPACITOR_PENDING_OAUTH_RETURN_KEY) ??
      localStorage.getItem(CAPACITOR_PENDING_OAUTH_RETURN_KEY)
    return value ? resolveOAuthCallbackUrl(value) : null
  } catch {
    return null
  }
}

export function hasPendingCapacitorOAuthReturn(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return Boolean(
      sessionStorage.getItem(CAPACITOR_PENDING_OAUTH_RETURN_KEY) ??
      localStorage.getItem(CAPACITOR_PENDING_OAUTH_RETURN_KEY)
    )
  } catch {
    return false
  }
}

export function stashCapacitorOAuthReturnPath(nextPath?: string | null): void {
  if (typeof window === 'undefined') return
  const path = resolveOAuthCallbackUrl(nextPath ?? DEFAULT_OAUTH_CALLBACK_URL)
  try {
    sessionStorage.setItem(CAPACITOR_PENDING_OAUTH_RETURN_KEY, path)
    localStorage.setItem(CAPACITOR_PENDING_OAUTH_RETURN_KEY, path)
  } catch {
    // ignore quota / private mode
  }
}

export function consumeCapacitorOAuthReturnPath(): string | null {
  if (typeof window === 'undefined') return null
  try {
    const value = peekCapacitorOAuthReturnPath()
    sessionStorage.removeItem(CAPACITOR_PENDING_OAUTH_RETURN_KEY)
    localStorage.removeItem(CAPACITOR_PENDING_OAUTH_RETURN_KEY)
    return value
  } catch {
    return null
  }
}

/**
 * Deep-link into the native Capacitor shell from Safari / ASWebAuthenticationSession.
 */
export async function issueCapacitorHandoffFromBrowser(): Promise<
  string | null
> {
  if (typeof window === 'undefined') return null
  try {
    const res = await fetch('/api/auth/capacitor-handoff/issue', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })
    if (!res.ok) return null
    const data = (await res.json()) as { handoff?: string }
    return data.handoff?.trim() || null
  } catch {
    return null
  }
}

export function returnToCapacitorNativeApp(
  nextPath?: string | null,
  handoffToken?: string | null
): void {
  if (typeof window === 'undefined') return
  const deepLink = buildCapacitorReturnDeepLink(nextPath, handoffToken)
  logCapacitorOAuth('deep_link_triggered', {
    nextPath: nextPath ?? DEFAULT_OAUTH_CALLBACK_URL,
    hasHandoff: Boolean(handoffToken),
    deepLink,
  })

  const openDeepLink = () => {
    try {
      const anchor = document.createElement('a')
      anchor.href = deepLink
      anchor.setAttribute('rel', 'noopener')
      anchor.style.display = 'none'
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
    } catch {
      // ignore
    }
    try {
      window.location.href = deepLink
    } catch {
      // ignore
    }
    try {
      window.location.replace(deepLink)
    } catch {
      // ignore
    }
  }

  openDeepLink()
  window.setTimeout(openDeepLink, 120)
  window.setTimeout(openDeepLink, 450)
}
